import React, { useState } from 'react';
import { useIntl } from 'react-intl';

import { IReportsResponse } from '@store/slices';

import { MONTHLY } from '../../../dashboard/dashboard-report';

import styles from './styles.module.scss';

const TOP_COUNT = 5;

interface IProps {
  data: IReportsResponse;
}

const useFormatters = () => {
  const intl = useIntl();
  return {
    money: (value: number | null | undefined) => intl.formatNumber(value || 0, { style: 'currency', currency: 'USD' }),
    count: (value: number | null | undefined) => intl.formatNumber(value || 0),
  };
};

interface IColumn<T> {
  key: string;
  labelId: string;
  numeric?: boolean;
  render: (row: T) => React.ReactNode;
}

interface IInsightTableProps<T> {
  id: string;
  titleId: string;
  rows: T[];
  columns: IColumn<T>[];
  rowKey: (row: T, index: number) => string;
}

/** A hub card holding a compact table: a header row, then rows, top five first. */
function InsightTable<T>({ id, titleId, rows, columns, rowKey }: IInsightTableProps<T>): React.ReactElement {
  const intl = useIntl();
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? rows : rows.slice(0, TOP_COUNT);

  return (
    <section className={styles.insightCard} aria-labelledby={id}>
      <div className={styles.insightHeader}>
        <h2 id={id} className={styles.insightTitle}>{intl.formatMessage({ id: titleId })}</h2>
      </div>

      {rows.length ? (
        <div className={styles.insightTableWrap}>
          <table className={styles.insightTable} aria-labelledby={id}>
            <thead>
              <tr>
                {columns.map(column => (
                  <th key={column.key} scope="col" className={column.numeric ? styles.numeric : undefined}>
                    {intl.formatMessage({ id: column.labelId })}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((row, index) => (
                <tr key={rowKey(row, index)}>
                  {columns.map(column => (
                    <td key={column.key} className={column.numeric ? styles.numeric : undefined}>{column.render(row)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className={styles.insightEmpty}>{intl.formatMessage({ id: 'my.account.hub.reports.no_data' })}</p>
      )}

      {rows.length > TOP_COUNT ? (
        <button type="button" className={styles.showAll} aria-expanded={showAll} onClick={() => setShowAll(value => !value)}>
          {intl.formatMessage(
            { id: showAll ? 'my.account.hub.reports.show_less' : 'my.account.hub.reports.show_all' },
            { count: rows.length },
          )}
        </button>
      ) : null}
    </section>
  );
}

interface IPeriodProps extends IProps {
  /** `MONTHLY` or `DAILY`, shared with the bookings total line chart. */
  period: string;
}

/**
 * Amount booked vs savings for the chart's period, as a CSS donut and a legend (replaces the
 * dashboard's pie, which needs a fixed tall box and printed unformatted amounts).
 */
export const AmountSavingsCard = ({ data, period }: IPeriodProps): React.ReactElement => {
  const intl = useIntl();
  const { money } = useFormatters();
  const entries = (period === MONTHLY ? data.bookingsTotalMonths : data.bookingsTotalDays) || [];
  const amount = entries.reduce((total, entry) => total + (entry.grossAmount || 0), 0);
  const savings = entries.reduce((total, entry) => total + (entry.savings || 0), 0);
  const whole = amount + savings;
  const savingsShare = whole > 0 ? (savings / whole) * 100 : 0;

  return (
    <section className={`${styles.insightCard} ${styles.donutCard}`} aria-labelledby="account-hub-amount-savings">
      <div className={styles.insightHeader}>
        <h2 id="account-hub-amount-savings" className={styles.insightTitle}>
          {intl.formatMessage({ id: 'my.account.hub.reports.amount_savings' })}
        </h2>
      </div>

      {whole > 0 ? (
        <>
          <div
            className={styles.donut}
            role="img"
            aria-label={intl.formatMessage(
              { id: 'my.account.hub.reports.amount_savings_label' },
              { amount: money(amount), savings: money(savings) },
            )}
          >
            <span className={styles.donutRing} style={{ '--savings-share': `${savingsShare}%` } as React.CSSProperties} aria-hidden="true" />
            <span className={styles.donutCenter} aria-hidden="true">
              <strong>{money(amount)}</strong>
              <span>{intl.formatMessage({ id: 'my.account.hub.reports.booked' })}</span>
            </span>
          </div>
          <dl className={styles.donutLegend}>
            <div>
              <dt><span className={`${styles.swatch} ${styles.swatchAmount}`} aria-hidden="true" />{intl.formatMessage({ id: 'my.account.hub.reports.amount' })}</dt>
              <dd>{money(amount)}</dd>
            </div>
            <div>
              <dt><span className={`${styles.swatch} ${styles.swatchSavings}`} aria-hidden="true" />{intl.formatMessage({ id: 'my.account.hub.reports.savings' })}</dt>
              <dd>{money(savings)}</dd>
            </div>
          </dl>
        </>
      ) : (
        <p className={styles.insightEmpty}>{intl.formatMessage({ id: 'my.account.hub.reports.no_data' })}</p>
      )}
    </section>
  );
};

/** Top destinations and top travelers as two half-width table cards (same report data). */
export const ReportInsights = ({ data }: IProps): React.ReactElement => {
  const intl = useIntl();
  const { money, count } = useFormatters();
  return (
    <div className={styles.insights}>
      <InsightTable
        id="account-hub-top-destinations"
        titleId="my.account.hub.reports.top_destinations"
        rows={data.topDestinationsHomes || []}
        rowKey={(row, index) => `${row.destination}-${index}`}
        columns={[
          {
            key: 'destination',
            labelId: 'my.account.hub.reports.col.destination',
            render: row => <span className={styles.cellName}>{row.destination?.trim() || intl.formatMessage({ id: 'my.account.hub.reports.unknown_destination' })}</span>,
          },
          { key: 'bookings', labelId: 'my.account.hub.reports.col.bookings', numeric: true, render: row => count(row.numberOfBookings) },
          { key: 'rooms', labelId: 'my.account.hub.reports.col.rooms', numeric: true, render: row => count(row.numberOfRooms) },
          {
            key: 'commission',
            labelId: 'my.account.hub.reports.col.commission',
            numeric: true,
            render: row => (
              <span className={styles.cellStack}>
                <strong>{money(row.commissions)}</strong>
                <span>{intl.formatMessage({ id: 'my.account.hub.reports.booked_amount' }, { amount: money(row.grossAmount) })}</span>
              </span>
            ),
          },
        ]}
      />

      <InsightTable
        id="account-hub-top-travelers"
        titleId="my.account.hub.reports.top_travelers"
        rows={data.topMembersHomes || []}
        rowKey={(row, index) => `${row.leadName}-${index}`}
        columns={[
          {
            key: 'name',
            labelId: 'my.account.hub.reports.col.lead_name',
            render: row => <span className={styles.cellName}>{row.leadName?.trim() || intl.formatMessage({ id: 'my.account.hub.reports.unknown_traveler' })}</span>,
          },
          { key: 'bookings', labelId: 'my.account.hub.reports.col.bookings', numeric: true, render: row => count(row.numberOfBookings) },
          { key: 'spent', labelId: 'my.account.hub.reports.col.total_spent', numeric: true, render: row => <strong>{money(row.totalSpent)}</strong> },
        ]}
      />
    </div>
  );
};
